import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Loader2, Infinity as InfinityIcon, Layers, ChevronRight, Save } from "lucide-react";
import { toast } from "sonner";
import { bots as botsApi } from "../lib/api";
import BottomNav from "../components/BottomNav";
import ConfirmDialog from "../components/ConfirmDialog";

export const BOT_DOT = {
    green: "bg-emerald-500", blue: "bg-sky-500", purple: "bg-violet-500",
    amber: "bg-amber-500", rose: "bg-rose-500", slate: "bg-slate-400", "": "bg-primary",
};
export const BOT_COLOR_IDS = ["", "green", "blue", "purple", "amber", "rose", "slate"];

export const RUN_STATUS = {
    running: { label: "Rodando", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
    paused:  { label: "Pausado", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
    stopped: { label: "Parado", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
    done:    { label: "Concluído", cls: "bg-sky-500/15 text-sky-400 border-sky-500/30" },
    error:   { label: "Erro", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
};

/**
 * /bots — tela inicial: seus GRUPOS de pipeline.
 * Cada grupo tem nome e configurações próprias (cadeia, tarefa, ciclos).
 * Clique num grupo para abrir a interface dele.
 */
export default function BotsPage() {
    const navigate = useNavigate();
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [toDelete, setToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const g = await botsApi.listGroups();
            setGroups(Array.isArray(g) ? g : []);
        } catch {
            toast.error("Não foi possível carregar seus grupos.");
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { refresh(); }, [refresh]);

    const confirmDelete = async () => {
        if (!toDelete) return;
        setDeleting(true);
        try {
            await botsApi.removeGroup(toDelete.id);
            setGroups((p) => p.filter((g) => g.id !== toDelete.id));
        } catch { toast.error("Não foi possível excluir o grupo."); }
        setDeleting(false);
        setToDelete(null);
    };

    return (
        <div className="h-[100dvh] bg-background flex flex-col overflow-hidden">
            <header className="shrink-0 h-12 md:h-14 border-b border-border bg-[#0A0A0A]/95 backdrop-blur-xl flex items-center gap-2 px-3 sm:px-6" data-testid="bots-header">
                <button type="button" onClick={() => navigate("/chat")} className="p-2 -ml-2 rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" aria-label="Voltar ao chat" data-testid="bots-back">
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <h1 className="font-display font-bold tracking-tight text-sm md:text-base">Pipeline de Bots</h1>
                <div className="flex-1" />
                <button type="button" onClick={() => setCreateOpen(true)} data-testid="create-group-btn"
                    className="btn-primary px-3.5 md:px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-1.5">
                    <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> Novo grupo
                </button>
            </header>

            <main className="flex-1 overflow-y-auto w-full">
                <div className="max-w-5xl mx-auto px-3 sm:px-6 py-4 md:py-6 pb-24 md:pb-10">
                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {[0, 1].map((i) => (
                                <div key={i} className="rounded-md border border-border bg-[#111111] p-4 min-h-[96px]" aria-busy="true">
                                    <div className="skeleton-bar h-3.5 w-1/2 rounded mb-2.5" />
                                    <div className="skeleton-bar h-2.5 w-3/4 rounded" />
                                </div>
                            ))}
                        </div>
                    ) : groups.length === 0 ? (
                        <div className="border border-dashed border-border rounded-xl p-10 text-center text-muted-foreground flex flex-col items-center gap-3">
                            <Layers className="w-8 h-8 opacity-50" />
                            <div className="text-sm font-medium text-foreground">Crie seu primeiro grupo de pipeline</div>
                            <div className="text-xs max-w-sm">Cada grupo tem seus próprios bots encadeados, tarefa e ciclos — perfeito para pesquisas aprofundadas em busca da verdade.</div>
                            <button type="button" onClick={() => setCreateOpen(true)} className="btn-primary mt-1 px-5 py-2 rounded-full text-xs font-semibold flex items-center gap-1.5">
                                <Plus className="w-3.5 h-3.5" /> Novo grupo
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {groups.map((g) => (
                                <div key={g.id} className="group relative rounded-md border border-border bg-[#111111] hover:border-primary/40 transition-colors" data-testid={`group-card-${g.id}`}>
                                    <button type="button" onClick={() => navigate(`/bots/g/${g.id}`)} className="w-full text-left p-4 pr-16" data-testid={`group-open-${g.id}`}>
                                        <div className="flex items-center gap-2">
                                            <Layers className="w-4 h-4 text-primary shrink-0" />
                                            <span className="font-semibold text-sm truncate">{g.name}</span>
                                        </div>
                                        <div className="text-[11px] text-muted-foreground mt-1.5 flex items-center gap-1.5">
                                            {(g.bot_ids || []).length} bot(s) na cadeia ·
                                            {g.max_cycles === 0 ? <InfinityIcon className="w-3 h-3" /> : ` ${g.max_cycles ?? 1} ciclo(s)`}
                                        </div>
                                        {g.input && <div className="text-[11px] text-muted-foreground/60 mt-1 line-clamp-1 italic">“{g.input}”</div>}
                                    </button>
                                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                                        <button type="button" onClick={() => setToDelete(g)} aria-label="Excluir grupo" data-testid={`group-delete-${g.id}`}
                                            className="p-2 rounded-sm text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                                        <ChevronRight className="w-4 h-4 text-muted-foreground/50" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </main>

            <BottomNav />

            {createOpen && (
                <CreateGroupDialog onClose={() => setCreateOpen(false)}
                    onCreate={async (name) => {
                        try {
                            const g = await botsApi.createGroup({ name, bot_ids: [], input: "", max_cycles: 1 });
                            setCreateOpen(false);
                            navigate(`/bots/g/${g.id}`);
                        } catch (e) {
                            toast.error(e?.response?.data?.detail || "Não foi possível criar o grupo.");
                        }
                    }} />
            )}

            <ConfirmDialog open={!!toDelete} title="Excluir grupo?" description={toDelete ? `“${toDelete.name}” e suas configurações serão removidos. Bots e execuções antigas continuam existindo.` : ""} confirmLabel="Excluir" destructive loading={deleting} onConfirm={confirmDelete} onCancel={() => setToDelete(null)} testId="group-delete-confirm" />
        </div>
    );
}

function CreateGroupDialog({ onClose, onCreate }) {
    const [name, setName] = useState("");
    const [saving, setSaving] = useState(false);
    const submit = async (e) => {
        e?.preventDefault?.();
        if (!name.trim()) { toast.error("Dê um nome ao grupo."); return; }
        setSaving(true);
        await onCreate(name.trim());
        setSaving(false);
    };
    return (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4 fade-in" data-testid="create-group-dialog">
            <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
            <form onSubmit={submit} className="relative w-full max-w-sm bg-[#111111] border border-border rounded-xl shadow-2xl p-5">
                <div className="font-display font-bold text-base mb-1">Novo grupo de pipeline</div>
                <div className="text-[11px] text-muted-foreground mb-3">Depois de criar, você monta a cadeia de bots e as configurações dele.</div>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoFocus data-testid="group-name-input"
                    placeholder="Ex.: Pesquisa da Verdade" className="w-full bg-[#141414] border border-border rounded-md px-3.5 py-2.5 text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 mb-4" />
                <div className="flex gap-2">
                    <button type="button" onClick={onClose} className="flex-1 py-2 rounded-full border border-border text-sm font-semibold text-muted-foreground hover:text-foreground">Cancelar</button>
                    <button type="submit" disabled={saving} data-testid="group-create-btn" className="btn-primary flex-1 py-2 rounded-full text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Criar
                    </button>
                </div>
            </form>
        </div>
    );
}
